import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useRef, useState } from "react";
import { usePortfolio } from "@/hooks/use-portfolio";
import {
  usePortfolioEligibleForListing,
  type ListingCandidate,
} from "@/hooks/use-portfolio-eligible-for-listing";
import { useRentals, RentalListing } from "@/hooks/use-rentals";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Sparkles,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Check,
  CheckCircle2,
  Building2,
  Save,
  CloudUpload,
  Link2,
} from "lucide-react";

// ── Constants ──────────────────────────────────────────────────────────────
const AMENITIES = [
  { key: "laundry", label: "In-unit laundry" },
  { key: "ac", label: "Central A/C" },
  { key: "dishwasher", label: "Dishwasher" },
  { key: "hardwood", label: "Hardwood floors" },
  { key: "updatedKitch", label: "Updated kitchen" },
  { key: "updatedBath", label: "Updated bath" },
  { key: "garage", label: "Garage" },
  { key: "yard", label: "Yard" },
  { key: "fenced", label: "Fenced yard" },
  { key: "basement", label: "Basement" },
  { key: "patio", label: "Patio / deck" },
] as const;

const UTILITIES = [
  { key: "water", label: "Water" },
  { key: "gas", label: "Gas" },
  { key: "electric", label: "Electric" },
  { key: "trash", label: "Trash" },
  { key: "internet", label: "Internet" },
] as const;

const CHANNELS_CONFIG = [
  {
    key: "zillow",
    label: "Zillow",
    sub: "Largest renter audience",
    initial: "Z",
    bg: "bg-[#006aff]",
  },
  {
    key: "apartments_com",
    label: "Apartments.com",
    sub: "Top apartment marketplace",
    initial: "A",
    bg: "bg-[#00838f]",
  },
  {
    key: "realtor_com",
    label: "Realtor.com",
    sub: "MLS-backed reach",
    initial: "R",
    bg: "bg-[#d92228]",
  },
  {
    key: "trulia",
    label: "Trulia",
    sub: "Bundled with Zillow",
    initial: "T",
    bg: "bg-[#00b04f]",
  },
  {
    key: "rent_com",
    label: "Rent.com",
    sub: "Lease-focused renters",
    initial: "R",
    bg: "bg-[#f5a623]",
  },
  {
    key: "facebook",
    label: "Facebook Marketplace",
    sub: "Local reach · Free",
    initial: "F",
    bg: "bg-[#1877f2]",
  },
  {
    key: "craigslist",
    label: "Craigslist",
    sub: "Local reach · Free",
    initial: "C",
    bg: "bg-[#5d2da9]",
  },
] as const;

const STEP_LABELS = [
  "Choose a property",
  "Set rental terms",
  "Write the listing",
  "Screening criteria",
  "Choose channels & publish",
];
const TOTAL_STEPS = STEP_LABELS.length;
const MAX_PHOTO_DIM = 1200;
const JPEG_QUALITY = 0.85;

// ── Types ──────────────────────────────────────────────────────────────────
interface WizardDraft {
  propertyId: string;
  title: string;
  description: string;
  rentAmount: string;
  deposit: string;
  beds: string;
  baths: string;
  sqft: string;
  leaseTerm: string;
  availableDate: string;
  petPolicy: string;
  utilities: string[];
  amenities: string[];
  photos: string[];
  minCredit: string;
  incomeMultiplier: string;
  backgroundCheck: boolean;
  acceptSection8: boolean;
  channels: string[];
}

const blankDraft = (): WizardDraft => ({
  propertyId: "",
  title: "",
  description: "",
  rentAmount: "",
  deposit: "",
  beds: "",
  baths: "",
  sqft: "",
  leaseTerm: "12",
  availableDate: "",
  petPolicy: "No pets",
  utilities: [],
  amenities: [],
  photos: [],
  minCredit: "650",
  incomeMultiplier: "3",
  backgroundCheck: true,
  acceptSection8: false,
  channels: [],
});

const draftFromListing = (l: RentalListing): WizardDraft => ({
  propertyId: l.pipeline_property_id,
  title: l.title,
  description: l.description ?? "",
  rentAmount: String(l.rent_amount),
  deposit: l.security_deposit != null ? String(l.security_deposit) : "",
  beds: l.beds != null ? String(l.beds) : "",
  baths: l.baths != null ? String(l.baths) : "",
  sqft: l.sqft != null ? String(l.sqft) : "",
  leaseTerm: String(l.lease_term_months ?? 12),
  availableDate: l.available_date ?? "",
  petPolicy: l.pet_policy ?? "No pets",
  utilities: l.utilities_included ?? [],
  amenities: l.amenities ?? [],
  photos: l.photos ?? [],
  // Fall back to the blank-draft defaults when null (e.g. Section 8 listings),
  // so the pickers always have a valid selection to show.
  minCredit:
    l.minimum_credit_score != null ? String(l.minimum_credit_score) : "650",
  incomeMultiplier:
    l.income_to_rent_ratio != null ? String(l.income_to_rent_ratio) : "3",
  backgroundCheck: l.background_check ?? true,
  acceptSection8: l.accept_section8_voucher ?? false,
  channels: [],
});

const RENTAL_PHOTOS_BUCKET = "rental-photos";

// Resize image via canvas then return as a Blob (not base64)
function compressToBlob(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Could not decode image"));
      img.onload = () => {
        const scale = Math.min(
          1,
          MAX_PHOTO_DIM / Math.max(img.width, img.height),
        );
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) =>
            blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")),
          "image/jpeg",
          JPEG_QUALITY,
        );
      };
      img.src = e.target!.result as string;
    };
    reader.readAsDataURL(file);
  });
}

// ── Shared micro components ────────────────────────────────────────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-[12px] font-semibold text-foreground flex items-center gap-1.5">
      {children}
    </label>
  );
}

function FieldHelp({ children }: { children: React.ReactNode }) {
  return <p className="text-[11.5px] text-muted-foreground">{children}</p>;
}

function MoneyInput({
  value,
  onChange,
  placeholder = "0",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="grid grid-cols-[auto_1fr] items-center bg-muted border border-border rounded-lg pl-3 transition-colors focus-within:border-destructive focus-within:bg-card">
      <span className="text-muted-foreground font-bold pr-2 select-none">
        $
      </span>
      <input
        type="number"
        min="0"
        step="25"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-0 outline-none focus:outline-none focus-visible:outline-none text-foreground py-[10px] pr-3 text-[13.5px] w-full tabular-nums"
      />
    </div>
  );
}

function Chip({
  label,
  checked,
  onClick,
}: {
  label: string;
  checked: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-3 py-[7px] rounded-full text-[12.5px] font-medium border cursor-pointer select-none transition-colors",
        checked
          ? "bg-destructive/12 border-destructive/28 text-destructive"
          : "bg-muted border-border text-muted-foreground hover:text-foreground hover:border-border/70",
      )}
    >
      {checked && <Check className="w-3 h-3 flex-shrink-0" />}
      {label}
    </button>
  );
}

function ToggleSwitch({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      className={cn(
        "relative w-[38px] h-[22px] rounded-full flex-shrink-0 transition-colors",
        on ? "bg-destructive" : "bg-border",
      )}
    >
      <span
        className={cn(
          "absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full transition-all",
          on
            ? "translate-x-4 bg-white"
            : "translate-x-0 bg-muted-foreground/60",
        )}
      />
    </button>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingListing?: RentalListing | null;
}

export function CreateListingDialog({
  open,
  onOpenChange,
  editingListing,
}: Props) {
  const isEdit = !!editingListing;

  const { portfolio: allPortfolio } = usePortfolio();
  const { eligiblePortfolio: candidates } = usePortfolioEligibleForListing();
  const { createListing, updateListing } = useRentals();
  const { user } = useAuth();

  // Create mode: closed-won properties parked in rehab (in_rehab = false).
  // Already-listed ones stay in the list but are flagged `alreadyListed` so the
  // picker disables them — no duplicate listings. Edit mode: show all portfolio
  // (we always need the listing's current property present) and never disable.
  const eligiblePortfolio = useMemo<ListingCandidate[]>(
    () =>
      isEdit
        ? allPortfolio.map((p) => ({
            id: p.id,
            property: p.property,
            alreadyListed: false,
            // Edit mode opens on step 2 and can't navigate back to the picker,
            // so no rent is ever pre-filled from here — the listing's own
            // rent_amount is authoritative.
            monthlyRent: null,
          }))
        : candidates,
    [allPortfolio, candidates, isEdit],
  );

  const [step, setStep] = useState(isEdit ? 2 : 1);
  const [draft, setDraft] = useState<WizardDraft>(
    isEdit && editingListing ? draftFromListing(editingListing) : blankDraft,
  );
  const [generating, setGenerating] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-init when editingListing changes (re-opening with different listing)
  useEffect(() => {
    if (open) {
      setDraft(
        editingListing ? draftFromListing(editingListing) : blankDraft(),
      );
      setStep(editingListing ? 2 : 1);
      setShowUrlInput(false);
      setPhotoUrl("");
      setDragOver(false);
    }
  }, [open, editingListing]);

  const set = <K extends keyof WizardDraft>(key: K, val: WizardDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: val }));

  const toggleList = (
    key: "utilities" | "amenities" | "channels",
    item: string,
  ) =>
    setDraft((d) => ({
      ...d,
      [key]: d[key].includes(item)
        ? d[key].filter((x) => x !== item)
        : [...d[key], item],
    }));

  const selectedProperty = eligiblePortfolio.find(
    (p) => p.id === draft.propertyId,
  );

  const resetAndClose = () => {
    onOpenChange(false);
  };

  // ── Validation ──────────────────────────────────────────────────────────
  const validate = () => {
    if (step === 1 && !draft.propertyId) {
      toast.error("Pick a property to continue");
      return false;
    }
    if (step === 2 && (!draft.rentAmount || Number(draft.rentAmount) < 1)) {
      toast.error("Enter a monthly rent");
      return false;
    }
    if (step === 3 && !draft.title.trim()) {
      toast.error("Add a listing headline");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (!validate()) return;
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }
    submitFinal("active");
  };

  // ── Submit ──────────────────────────────────────────────────────────────
  const submitFinal = (status: "draft" | "active") => {
    if (!draft.propertyId) {
      toast.error("Select a property first");
      return;
    }
    const payload = {
      pipeline_property_id: draft.propertyId,
      title:
        draft.title || selectedProperty?.property?.address || "New Listing",
      description: draft.description || null,
      rent_amount: Number(draft.rentAmount) || 0,
      security_deposit: draft.deposit ? Number(draft.deposit) : null,
      beds: draft.beds ? Number(draft.beds) : null,
      baths: draft.baths ? Number(draft.baths) : null,
      sqft: draft.sqft ? Number(draft.sqft) : null,
      lease_term_months: Number(draft.leaseTerm),
      available_date: draft.availableDate || null,
      pet_policy: draft.petPolicy,
      utilities_included: draft.utilities.length ? draft.utilities : null,
      amenities: draft.amenities.length ? draft.amenities : null,
      photos: draft.photos.length ? draft.photos : null,
      contact_name: null,
      contact_phone: null,
      contact_email: null,
      template_id: null,
      // Credit score / income ratio don't apply to Section 8 (the voucher covers
      // rent), so persist null when vouchers are accepted — matches the wizard
      // hiding those fields in that case.
      minimum_credit_score: draft.acceptSection8
        ? null
        : draft.minCredit
          ? Number(draft.minCredit)
          : null,
      income_to_rent_ratio: draft.acceptSection8
        ? null
        : draft.incomeMultiplier
          ? Number(draft.incomeMultiplier)
          : null,
      background_check: draft.backgroundCheck,
      accept_section8_voucher: draft.acceptSection8,
    };

    if (isEdit && editingListing) {
      updateListing.mutate(
        { id: editingListing.id, ...payload },
        { onSuccess: () => resetAndClose() },
      );
    } else {
      createListing.mutate(
        { ...payload, status },
        { onSuccess: () => resetAndClose() },
      );
    }
  };

  const isPending = createListing.isPending || updateListing.isPending;

  // ── AI description ──────────────────────────────────────────────────────
  const generateDescription = async () => {
    if (!selectedProperty) return;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "generate-listing-description",
        {
          body: {
            property: selectedProperty.property,
            listing: {
              title: draft.title,
              rent_amount: Number(draft.rentAmount) || 0,
              lease_term_months: Number(draft.leaseTerm),
              pet_policy: draft.petPolicy,
            },
            photoUrls: [],
          },
        },
      );
      if (error) throw error;
      if (data?.description) {
        set("description", data.description);
        toast.success("Description generated!");
      }
    } catch {
      toast.error("Failed to generate description");
    } finally {
      setGenerating(false);
    }
  };

  // ── Photo ingestion — compress then upload to storage ───────────────────
  const ingestFiles = async (files: FileList | null) => {
    if (!files?.length || !user) return;
    const imgs = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!imgs.length) return;
    setUploadingPhotos(true);
    const newUrls: string[] = [];
    try {
      for (const file of imgs) {
        const blob = await compressToBlob(file);
        const listingSegment =
          editingListing?.id ?? `draft-${crypto.randomUUID()}`;
        const path = `${user.id}/${listingSegment}/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
        const { error } = await supabase.storage
          .from(RENTAL_PHOTOS_BUCKET)
          .upload(path, blob, { contentType: "image/jpeg" });
        if (error) {
          toast.error(`Failed to upload ${file.name}`);
          continue;
        }
        const { data: urlData } = supabase.storage
          .from(RENTAL_PHOTOS_BUCKET)
          .getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
      if (newUrls.length) {
        setDraft((d) => ({ ...d, photos: [...d.photos, ...newUrls] }));
      }
    } catch {
      toast.error("Failed to upload one or more photos");
    } finally {
      setUploadingPhotos(false);
    }
  };

  const addPhotoUrl = () => {
    const url = photoUrl.trim();
    if (!url) return;
    setDraft((d) => ({ ...d, photos: [...d.photos, url] }));
    setPhotoUrl("");
    setShowUrlInput(false);
  };

  // ── Step renderers ──────────────────────────────────────────────────────
  const step1 = () => (
    <div className="space-y-3">
      <FieldHelp>
        Pick which of your owned units this listing is for. Already-listed
        properties are disabled.
      </FieldHelp>
      {eligiblePortfolio.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No eligible properties. A property must be in Closed-Won and out of
          active rehab before you can list it for rent.
        </div>
      ) : (
        <div className="space-y-2.5">
          {eligiblePortfolio.map((p) => {
            const prop = p.property as any;
            const addr1 = prop?.address || prop?.street_address || "Property";
            const addr2 = [prop?.city, prop?.state].filter(Boolean).join(", ");
            const imageUrl = prop?.imageUrl || prop?.image_url;
            const sqft = prop?.sqft ?? prop?.square_feet;
            const beds = prop?.bedrooms ?? prop?.beds;
            const baths = prop?.bathrooms ?? prop?.baths;
            // Rent comes from `property_setting.monthly_rent` (the underwritten
            // figure), never from the `property_data` listing estimate.
            const rent = p.monthlyRent;
            const specs = [
              prop?.bedrooms && `${prop.bedrooms} bd`,
              prop?.bathrooms && `${prop.bathrooms} ba`,
              sqft && `${sqft} sf`,
            ]
              .filter(Boolean)
              .join(" · ");
            const selected = draft.propertyId === p.id;
            const disabled = p.alreadyListed;
            return (
              <button
                key={p.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  set("propertyId", p.id);
                  if (!draft.title && addr1) set("title", addr1);
                  if (!draft.rentAmount && rent)
                    set("rentAmount", String(rent));
                  if (!draft.deposit && rent) set("deposit", String(rent));
                  if (!draft.beds && beds != null) set("beds", String(beds));
                  if (!draft.baths && baths != null)
                    set("baths", String(baths));
                  if (!draft.sqft && sqft != null) set("sqft", String(sqft));
                  // Seed the property's own image as the cover photo so the
                  // listing isn't saved photo-less when the user skips uploads.
                  if (!draft.photos.length && imageUrl)
                    set("photos", [imageUrl]);
                }}
                className={cn(
                  "w-full grid grid-cols-[72px_1fr_auto] gap-3.5 items-center p-3 rounded-[10px] border transition-colors text-left",
                  disabled
                    ? "border-border bg-muted/50 opacity-60 cursor-not-allowed"
                    : selected
                      ? "border-destructive bg-destructive/6"
                      : "border-border bg-muted hover:border-border/70",
                )}
              >
                <div className="w-[72px] h-[72px] rounded-lg bg-card flex items-center justify-center overflow-hidden border border-border flex-shrink-0">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="w-7 h-7 text-muted-foreground/30" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-[13.5px] text-foreground truncate">
                    {addr1}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">
                    {addr2}
                  </div>
                  {specs && (
                    <div className="text-[11.5px] text-muted-foreground/70 mt-1">
                      {specs}
                    </div>
                  )}
                </div>
                {disabled ? (
                  <span className="text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground bg-border/60 rounded px-2 py-1 flex-shrink-0">
                    Listed
                  </span>
                ) : (
                  <div
                    className={cn(
                      "w-[22px] h-[22px] rounded-full border-[1.5px] grid place-items-center flex-shrink-0",
                      selected ? "border-destructive" : "border-border",
                    )}
                  >
                    {selected && (
                      <span className="w-2.5 h-2.5 rounded-full bg-destructive" />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const step2 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3.5">
        <div className="space-y-1.5">
          <FieldLabel>Bedrooms</FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            value={draft.beds}
            placeholder="0"
            onChange={(e) => set("beds", e.target.value)}
            className="bg-muted border-border focus-visible:border-destructive focus-visible:ring-0 tabular-nums"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Bathrooms</FieldLabel>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={draft.baths}
            placeholder="0"
            onChange={(e) => set("baths", e.target.value)}
            className="bg-muted border-border focus-visible:border-destructive focus-visible:ring-0 tabular-nums"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Square feet</FieldLabel>
          <Input
            type="number"
            min="0"
            step="50"
            value={draft.sqft}
            placeholder="0"
            onChange={(e) => set("sqft", e.target.value)}
            className="bg-muted border-border focus-visible:border-destructive focus-visible:ring-0 tabular-nums"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        <div className="space-y-1.5">
          <FieldLabel>Monthly rent</FieldLabel>
          <MoneyInput
            value={draft.rentAmount}
            onChange={(v) => set("rentAmount", v)}
          />
          <FieldHelp>
            Pre-filled from underwriting; edit to your asking price.
          </FieldHelp>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Security deposit</FieldLabel>
          <MoneyInput
            value={draft.deposit}
            onChange={(v) => set("deposit", v)}
          />
          <FieldHelp>Typically 1 month's rent.</FieldHelp>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3.5">
        <div className="space-y-1.5">
          <FieldLabel>Available from</FieldLabel>
          <DateInput
            value={draft.availableDate}
            onChange={(e) => set("availableDate", e.target.value)}
            className="bg-muted border-border focus:border-destructive"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>Lease length</FieldLabel>
          <Select
            value={draft.leaseTerm}
            onValueChange={(v) => set("leaseTerm", v)}
          >
            <SelectTrigger className="bg-muted border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Month-to-month</SelectItem>
              <SelectItem value="6">6 months</SelectItem>
              <SelectItem value="12">12 months</SelectItem>
              <SelectItem value="24">24 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>Pet policy</FieldLabel>
        <Select
          value={draft.petPolicy}
          onValueChange={(v) => set("petPolicy", v)}
        >
          <SelectTrigger className="bg-muted border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="No pets">No pets</SelectItem>
            <SelectItem value="Cats only">Cats only</SelectItem>
            <SelectItem value="Dogs only">Dogs only</SelectItem>
            <SelectItem value="Cats and dogs">Cats &amp; dogs</SelectItem>
            <SelectItem value="All pets welcome">All pets welcome</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <FieldLabel>Utilities included</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {UTILITIES.map((u) => (
            <Chip
              key={u.key}
              label={u.label}
              checked={draft.utilities.includes(u.key)}
              onClick={() => toggleList("utilities", u.key)}
            />
          ))}
        </div>
        <FieldHelp>Toggle utilities covered in the rent.</FieldHelp>
      </div>
    </div>
  );

  const step3 = () => (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>Listing headline</FieldLabel>
        <Input
          value={draft.title}
          maxLength={80}
          onChange={(e) => set("title", e.target.value)}
          placeholder="Spacious 3-bed near downtown"
          className="bg-muted border-border focus-visible:border-destructive focus-visible:ring-0"
        />
        <FieldHelp>
          Shown at the top of the listing. Up to 80 characters.
        </FieldHelp>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <FieldLabel>Description</FieldLabel>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={generateDescription}
            disabled={generating || !draft.propertyId}
            className="gap-1.5 text-xs h-7"
          >
            {generating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5 text-accent" />
            )}
            {generating ? "Generating…" : "AI Generate"}
          </Button>
        </div>
        <Textarea
          value={draft.description}
          rows={4}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Talk about the neighborhood, recent updates, what makes this unit stand out…"
          className={cn(
            "bg-muted border-border focus-visible:border-destructive focus-visible:ring-0 resize-none",
            generating && "opacity-50",
          )}
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>Amenities</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {AMENITIES.map((a) => (
            <Chip
              key={a.key}
              label={a.label}
              checked={draft.amenities.includes(a.key)}
              onClick={() => toggleList("amenities", a.key)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel>Photos</FieldLabel>
        {/* Photo grid */}
        {draft.photos.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-2">
            {draft.photos.map((src, i) => (
              <div
                key={i}
                className="relative aspect-[4/3] rounded-lg overflow-hidden bg-muted border border-border group"
              >
                <img
                  src={src}
                  alt={`Photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                {i === 0 && (
                  <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-px rounded-[4px]">
                    Cover
                  </span>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      photos: d.photos.filter((_, j) => j !== i),
                    }))
                  }
                  className="absolute top-1 right-1 w-[22px] h-[22px] rounded-full bg-black/70 text-white grid place-items-center opacity-0 group-hover:opacity-100 hover:bg-destructive transition-all text-base leading-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Dropzone */}
        <div
          className={cn(
            "border border-dashed rounded-[10px] p-5 flex flex-col items-center gap-2.5 transition-colors",
            dragOver ? "border-destructive bg-destructive/6" : "border-border",
          )}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
          }}
          onDrop={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            await ingestFiles(e.dataTransfer.files);
          }}
        >
          <CloudUpload className="w-6 h-6 text-muted-foreground/60" />
          <p className="text-[12.5px] text-muted-foreground text-center">
            Drag &amp; drop image files here, or use a button below.
          </p>
          <div className="flex gap-2 flex-wrap justify-center">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={async (e) => {
                await ingestFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={uploadingPhotos}
              onClick={() => fileInputRef.current?.click()}
              className="gap-1.5 text-xs"
            >
              {uploadingPhotos ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <CloudUpload className="w-3.5 h-3.5" /> Upload photos
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowUrlInput((v) => !v);
              }}
              className="gap-1.5 text-xs"
            >
              <Link2 className="w-3.5 h-3.5" /> Add by URL
            </Button>
          </div>
          {showUrlInput && (
            <div className="flex gap-2 w-full max-w-sm">
              <Input
                value={photoUrl}
                autoFocus
                placeholder="https://…"
                onChange={(e) => setPhotoUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPhotoUrl();
                  }
                }}
                className="flex-1 bg-muted text-sm h-8"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={addPhotoUrl}
                className="h-8"
              >
                Add
              </Button>
            </div>
          )}
        </div>
        <FieldHelp>
          First photo is the cover. Uploads are resized to {MAX_PHOTO_DIM}px
          before saving.
        </FieldHelp>
      </div>
    </div>
  );

  const step4 = () => (
    <div className="space-y-4">
      <FieldHelp>
        Set the bar applicants need to clear before you'll consider them.
      </FieldHelp>
      {/* Credit score + income ratio don't apply to Section 8 — the voucher
          covers rent, so these screens are hidden when vouchers are accepted. */}
      {!draft.acceptSection8 && (
        <div className="grid grid-cols-2 gap-3.5">
          <div className="space-y-1.5">
            <FieldLabel>Minimum credit score</FieldLabel>
            <Select
              value={draft.minCredit}
              onValueChange={(v) => set("minCredit", v)}
            >
              <SelectTrigger className="bg-muted border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[600, 650, 680, 700, 720, 750].map((v) => (
                  <SelectItem key={v} value={String(v)}>
                    {v}+
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Income to rent ratio</FieldLabel>
            <Select
              value={draft.incomeMultiplier}
              onValueChange={(v) => set("incomeMultiplier", v)}
            >
              <SelectTrigger className="bg-muted border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2, 2.5, 3, 3.5].map((v) => (
                  <SelectItem key={v} value={String(v)}>
                    {v}× monthly rent
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      {[
        {
          key: "backgroundCheck" as const,
          title: "Background & eviction check",
          help: "Require a 3rd-party background check before approving an application.",
        },
        {
          key: "acceptSection8" as const,
          title: "Accept Section 8 vouchers",
          help: "Allow applicants paying with Housing Choice Vouchers. Recommended in many markets.",
        },
      ].map((row) => (
        <div
          key={row.key}
          className="flex items-start justify-between gap-3 p-3.5 rounded-[10px] bg-muted border border-border"
        >
          <div>
            <div className="text-[13px] font-semibold text-foreground">
              {row.title}
            </div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5 max-w-[380px]">
              {row.help}
            </div>
          </div>
          <ToggleSwitch
            on={draft[row.key]}
            onToggle={() => set(row.key, !draft[row.key])}
          />
        </div>
      ))}
    </div>
  );

  const step5 = () => {
    const prop = selectedProperty?.property as any;
    const addr1 =
      prop?.address || prop?.street_address || "Pick a property in step 1";
    const addr2 = [prop?.city, prop?.state].filter(Boolean).join(", ");
    // Prefer an uploaded photo as the hero; otherwise fall back to the
    // property's own image so the preview isn't blank.
    const hero = draft.photos[0] || prop?.imageUrl || prop?.image_url;
    const fmtMoney = (n: number) =>
      "$" + Math.round(Math.abs(n)).toLocaleString("en-US");

    return (
      <div className="space-y-5">
        {/* Channel grid */}
        <div className="space-y-2">
          <FieldLabel>Syndicate to</FieldLabel>
          <FieldHelp>
            Pick the marketplaces this listing should appear on. Toggle channels
            on or off any time after publishing.
          </FieldHelp>
          <div className="grid grid-cols-2 gap-2.5 mt-1">
            {CHANNELS_CONFIG.map((c) => {
              const on = draft.channels.includes(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleList("channels", c.key)}
                  className={cn(
                    "grid grid-cols-[36px_1fr_auto] gap-3 items-center p-3 rounded-[10px] border transition-colors text-left",
                    on
                      ? "border-destructive bg-destructive/6"
                      : "border-border bg-muted hover:border-border/70",
                  )}
                >
                  <div
                    className={cn(
                      "w-9 h-9 rounded-lg grid place-items-center text-sm font-black text-white flex-shrink-0",
                      c.bg,
                    )}
                  >
                    {c.initial}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-foreground truncate">
                      {c.label}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground mt-px truncate">
                      {c.sub}
                    </div>
                  </div>
                  {/* inline switch visual */}
                  <div
                    className={cn(
                      "relative w-[38px] h-[22px] rounded-full flex-shrink-0 pointer-events-none transition-colors",
                      on ? "bg-destructive" : "bg-border",
                    )}
                  >
                    <span
                      className={cn(
                        "absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full transition-all",
                        on
                          ? "translate-x-4 bg-white"
                          : "bg-muted-foreground/60",
                      )}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Final preview */}
        <div className="space-y-2">
          <FieldLabel>Final preview</FieldLabel>
          <div className="bg-muted border border-border rounded-[10px] overflow-hidden">
            <div
              className="h-[140px] bg-card bg-cover bg-center"
              style={hero ? { backgroundImage: `url('${hero}')` } : {}}
            >
              {!hero && (
                <div className="w-full h-full flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-muted-foreground/20" />
                </div>
              )}
            </div>
            <div className="p-4 space-y-2">
              <div>
                <div className="font-bold text-[15px] text-foreground">
                  {addr1}
                </div>
                {addr2 && (
                  <div className="text-[12.5px] text-muted-foreground mt-0.5">
                    {addr2}
                  </div>
                )}
              </div>
              {draft.title && (
                <div className="text-[13.5px] font-semibold text-foreground">
                  {draft.title}
                </div>
              )}
              {draft.description && (
                <div className="text-[12.5px] text-muted-foreground leading-relaxed line-clamp-2">
                  {draft.description}
                </div>
              )}
              <div className="pt-1 space-y-0">
                {[
                  [
                    "Rent",
                    draft.rentAmount
                      ? fmtMoney(Number(draft.rentAmount)) + "/mo"
                      : "—",
                  ],
                  [
                    "Deposit",
                    draft.deposit ? fmtMoney(Number(draft.deposit)) : "—",
                  ],
                  ["Available", draft.availableDate || "—"],
                  [
                    "Lease",
                    draft.leaseTerm === "0"
                      ? "Month-to-month"
                      : draft.leaseTerm + " months",
                  ],
                  [
                    "Section 8",
                    draft.acceptSection8 ? "Accepted" : "Not accepted",
                  ],
                  [
                    "Channels",
                    draft.channels.length
                      ? `${draft.channels.length} selected`
                      : "None",
                  ],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between text-[12.5px] py-1.5 border-b border-border/50 last:border-0"
                  >
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-semibold text-foreground">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const STEPS = [step1, step2, step3, step4, step5];

  // ── Modal ───────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-[720px] p-0 gap-0 overflow-hidden max-h-[calc(100vh-48px)] flex flex-col">
        {/* Header */}
        <div className="px-[22px] pt-[18px] pb-[14px] border-b border-border flex-shrink-0">
          {/* Close button is provided by DialogContent (top-right) — don't
              add a second one here. */}
          <div>
            <h2 className="text-[18px] font-bold text-foreground">
              {isEdit ? "Edit listing" : "List a property for rent"}
            </h2>
            <div className="text-[12.5px] text-muted-foreground mt-1">
              Step {step} of {TOTAL_STEPS} — {STEP_LABELS[step - 1]}
            </div>
          </div>
          {/* Stepper segments */}
          <div className="flex items-center gap-1 mt-3">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "flex-1 h-1 rounded-sm transition-colors",
                  i < step ? "bg-destructive" : "bg-border",
                )}
              />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-[22px] py-[22px] overflow-y-auto flex-1 min-h-0">
          {STEPS[step - 1]()}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-[22px] py-[14px] border-t border-border gap-2.5 flex-shrink-0">
          {!isEdit ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => submitFinal("draft")}
              disabled={!draft.propertyId || isPending}
              className="gap-1.5"
            >
              <Save className="w-3.5 h-3.5" /> Save as draft
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2.5 items-center">
            {step > (isEdit ? 2 : 1) && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              disabled={isPending}
              className="gap-1.5"
            >
              {isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : step === TOTAL_STEPS ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />{" "}
                  {isEdit
                    ? "Save changes"
                    : `Publish to ${draft.channels.length || 0} channel${draft.channels.length !== 1 ? "s" : ""}`}
                </>
              ) : (
                <>
                  Next <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
